import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import { PaintBrush } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import z from 'zod';
import {
  INode,
  ITypeNode,
  useFormBuilder,
} from '../../Context/FormBuilderContext';
import { ActionTypes } from '../../Reducer';
import { CustomizationForm } from '../FormCustomization/CustomizationForm';

interface OutputSettingsProps {
  node: INode;
}

const OutputSchema = z.object({
  title: z.string(),
  subtitle: z.string(),
  varDataOutputId: z.string().optional(),
  fieldType: z
    .enum(['standard', 'editor', 'data', 'both', 'upload'])
    .default('standard'),
  fontFamily: z.string().default('Roboto, sans-serif'),
  fontSize: z.number().min(8).max(72).default(16),
  isBold: z.boolean().default(false),
  isItalic: z.boolean().default(false),
  isUnderlined: z.boolean().default(false),
  textAlign: z.enum(['left', 'center', 'right', 'justify']).default('left'),
  titleColor: z.string().default('#1F2937'),
  subtitleColor: z.string().default('#1F2937'),
  subtitleFontFamily: z.string().default('Roboto, sans-serif'),
  subtitleFontSize: z.number().min(8).max(72).default(16),
  isSubtitleBold: z.boolean().default(false),
  isSubtitleItalic: z.boolean().default(false),
  isSubtitleUnderlined: z.boolean().default(false),
  subtitleTextAlign: z
    .enum(['left', 'center', 'right', 'justify'])
    .default('left'),
  backgroundColor: z.string().default('#ffffff'),
  borderColor: z.string().default('#ccc'),
  borderRadius: z.number().min(4).max(72).default(4),
  borderWeight: z.number().min(1).max(10).default(1),
  verticalAlign: z.enum(['top', 'center', 'bottom']).default('top'),
  padding: z.number().min(0).max(72).default(16),
});

interface DataOutputNode {
  id: string;
  label: string;
  type: string;
}

export function OutputSettings({ node }: OutputSettingsProps) {
  const { dispatch, state } = useFormBuilder();
  const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const styles = node?.data?.styles;

  const formsMethods = useForm({
    defaultValues: {
      title: node?.data?.title || 'Title',
      subtitle: node?.data?.subtitle || 'Subtitle',
      varDataOutputId: node?.data.varDataOutputId || '',
      fieldType: node?.data?.fieldType || 'standard',
      fontFamily: styles?.title?.fontFamily || 'Roboto, sans-serif',
      fontSize: styles?.title?.fontSize || 16,
      isBold: styles?.title?.isBold || false,
      isItalic: styles?.title?.isItalic || false,
      isUnderlined: styles?.title?.isUnderlined || false,
      textAlign: styles?.title?.textAlign || 'left',
      titleColor: styles?.title?.color || '#1F2937',
      subtitleColor: styles?.subtitle?.color || '#1F2937',
      subtitleFontFamily: styles?.subtitle?.fontFamily || 'Roboto, sans-serif',
      subtitleFontSize: styles?.subtitle?.fontSize || 16,
      isSubtitleBold: styles?.subtitle?.isBold || false,
      isSubtitleItalic: styles?.subtitle?.isItalic || false,
      isSubtitleUnderlined: styles?.subtitle?.isUnderlined || false,
      subtitleTextAlign: styles?.subtitle?.textAlign || 'left',
      backgroundColor: node?.data?.styles?.backgroundColor || '#ffffff',
      borderColor: node?.data?.styles?.borderColor || '#3650DE',
      borderRadius: node?.data?.styles?.borderRadius || 4,
      borderWeight: node?.data?.styles?.borderWeight || 1,
      verticalAlign: node?.data?.styles?.verticalAlign || 'top',
      padding: node?.data?.styles?.padding !== undefined ? node.data.styles.padding : 16,
    },
    resolver: zodResolver(OutputSchema),
  });

  const handleNodeSelect = (node: DataOutputNode) => {
    setExpandedNodeId(expandedNodeId === node.id ? null : node.id);
    formsMethods.setValue('varDataOutputId', node.id);
  };

  const handleFormChange = (data: any) => {
    const updatedNode = {
      ...node,
      data: {
        ...node.data,
        title: data.title,
        subtitle: data.subtitle,
        varDataOutputId: data.varDataOutputId,
        fieldType: data.fieldType,
        styles: {
          title: {
            fontFamily: data.fontFamily,
            fontSize: data.fontSize,
            isBold: data.isBold,
            isItalic: data.isItalic,
            isUnderlined: data.isUnderlined,
            textAlign: data.textAlign,
            color: data.titleColor,
          },
          subtitle: {
            fontFamily: data.subtitleFontFamily,
            fontSize: data.subtitleFontSize,
            isBold: data.isSubtitleBold,
            isItalic: data.isSubtitleItalic,
            isUnderlined: data.isSubtitleUnderlined,
            textAlign: data.subtitleTextAlign,
            color: data.subtitleColor,
          },
          backgroundColor: data.backgroundColor,
          borderColor: data.borderColor,
          borderRadius: data.borderRadius,
          borderWeight: data.borderWeight,
          verticalAlign: data.verticalAlign,
          padding: data.padding,
        },
      },
    };

    dispatch({
      type: ActionTypes.UPDATE_NODES_DATA,
      payload: {
        id: node.id,
        updates: { data: updatedNode.data },
      },
    });
  };

  useEffect(() => {
    const subscription = formsMethods.watch(data => {
      if (data && Object.keys(data).length > 0) {
        handleFormChange(data);
      }
    });

    return () => subscription.unsubscribe();
  }, [formsMethods.watch, node.id]);

  const formValues = formsMethods.watch();

  const selectedNodeData = state.varOutputNodes.find(
    node => node.id === formValues.varDataOutputId,
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '16px 16px 0 16px',
        }}
      >
        <PaintBrush size={16} color="#101828" />
        <Typography
          style={{ fontSize: '14px', fontWeight: '500', color: '#101828' }}
        >
          Output Settings
        </Typography>
      </Box>

      <FormProvider {...formsMethods}>
        <form style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box
            sx={{
              display: 'flex',
              gap: '4px',
              padding: '0px 16px 0 16px',
            }}
          >
            <Box
              onClick={() => setActiveTab(0)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 0 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 0 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 0 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Content
            </Box>

            <Box
              onClick={() => setActiveTab(1)}
              sx={{
                px: 2,
                py: 1,
                cursor: 'pointer',
                color: activeTab === 1 ? '#4F46E5' : '#6B7280',
                backgroundColor: activeTab === 1 ? '#F3F4F6' : 'transparent',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: activeTab === 1 ? '#F3F4F6' : '#F9FAFB',
                },
              }}
            >
              Design
            </Box>
          </Box>

          <div
            style={{ width: '100%', height: '1px', backgroundColor: '#EAECF0' }}
          />

          {activeTab === 0 && (
            <Box sx={{ padding: '0px 16px 0 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Controller
                name="title"
                control={formsMethods.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Title"
                    placeholder="Enter title..."
                    fullWidth
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        margin: '0px',
                        fontSize: '14px',
                        height: '38px',
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="subtitle"
                control={formsMethods.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Subtitle"
                    placeholder="Enter subtitle..."
                    fullWidth
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        margin: '0px',
                        fontSize: '14px',
                        height: '38px',
                      },
                    }}
                  />
                )}
              />

              <Typography
                style={{
                  fontSize: '12px',
                  fontWeight: '400',
                  color: '#475467',
                }}
              >
                Please select which "Data output" to use for this output field
              </Typography>

              {selectedNodeData && (
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                >
                  <Typography
                    style={{
                      fontSize: '12px',
                      fontWeight: '400',
                      color: '#475467',
                    }}
                  >
                    Node selected:
                  </Typography>

                  <div
                    style={{
                      backgroundColor: '#F9FAFB',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #E4E7EC',
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{ fontWeight: '500', marginBottom: '4px' }}
                    >
                      Selected: {selectedNodeData.label}
                    </Typography>
                    <Typography variant="caption" style={{ color: '#475467' }}>
                      Type: {formValues?.fieldType}
                    </Typography>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                <Typography
                  style={{
                    fontSize: '12px',
                    fontWeight: '400',
                    color: '#475467',
                  }}
                >
                  Select data output
                </Typography>

                {state.varOutputNodes.map(node => (
                  <div
                    key={node.id}
                    style={{
                      border: '1px solid #E4E7EC',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <div
                      onClick={() => handleNodeSelect(node)}
                      style={{
                        padding: '12px',
                        cursor: 'pointer',
                        backgroundColor:
                          expandedNodeId === node.id ? '#F0F4FF' : 'transparent',
                        borderBottom:
                          expandedNodeId === node.id ? '1px solid #E4E7EC' : 'none',
                      }}
                      onMouseEnter={e => {
                        if (expandedNodeId !== node.id) {
                          e.currentTarget.style.backgroundColor = '#F9FAFB';
                          e.currentTarget.style.borderColor = '#3650DE';
                        }
                      }}
                      onMouseLeave={e => {
                        if (expandedNodeId !== node.id) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = '#E4E7EC';
                        }
                      }}
                    >
                      <Typography variant="subtitle2" style={{ fontWeight: '500' }}>
                        {node.label}
                      </Typography>
                    </div>

                    {expandedNodeId === node.id && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          padding: '16px',
                          backgroundColor: '#FAFBFC',
                          gap: '8px',
                        }}
                      >
                        <FormControl component="fieldset">
                          <Controller
                            name="fieldType"
                            control={formsMethods.control}
                            render={({ field }) => (
                              <RadioGroup
                                value={field.value}
                                onChange={e => field.onChange(e.target.value)}
                                style={{ flexDirection: 'row' }}
                              >
                                <FormControlLabel
                                  value="standard"
                                  control={<Radio size="small" />}
                                  label="Standard"
                                />
                                <FormControlLabel
                                  value="editor"
                                  control={<Radio size="small" />}
                                  label="Editor"
                                />
                              </RadioGroup>
                            )}
                          />
                        </FormControl>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ padding: '0px 16px 0 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <CustomizationForm
                control={formsMethods.control}
                type={ITypeNode.OUTPUT}
              />
            </Box>
          )}
        </form>
      </FormProvider>
    </>
  );
}
